import ConfirmModal from 'components/ConfirmModal';
import { defer, noop } from 'lodash-es';
import {
  FormEvent,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Form as FinalForm,
  FormProps as FinalFormProps,
  FormSpy
} from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Location } from 'history';
import * as yup from 'yup';
import { useUnmount } from 'react-use';
import { FormContext } from './FormContext';
import { getValidator } from './utils';
import { RegisterFieldProvider } from './RegisterField';

interface FormProps<T> extends FinalFormProps<T> {
  resourceName: string;
  className?: string;
  schema: yup.AnySchema;
  allowLeave?: boolean;
  hideModal?: boolean;
  children: ReactNode | ReactNode[];
  onChange?: (data: T) => void;
  readOnly?: boolean;
}

const preventDefault = (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  e.stopPropagation();
};

const SUBSCRIPBTION = { values: true };

function FormBase<T>({
  children,
  resourceName,
  className,
  schema,
  allowLeave,
  hideModal,
  onChange,
  readOnly,
  ...props
}: FormProps<T>) {
  const validator = useMemo(() => getValidator(schema), [schema]);
  const [targetLocation, setTargetLocation] = useState<Location | undefined>();
  const unblock = useRef(noop);

  const history = useHistory();

  const clearTargetLocation = useCallback(() => {
    setTargetLocation(undefined);
    unblock.current();
  }, [setTargetLocation]);

  const handleRedirect = useCallback(() => {
    unblock.current();
    if (targetLocation) {
      history.push(targetLocation);
    }
  }, [history, targetLocation]);

  useUnmount(() => unblock.current());

  const { t } = useTranslation('ui');

  const formContextValue = useMemo(
    () => ({ resourceName, readOnly: Boolean(readOnly) }),
    [resourceName, readOnly]
  );

  return (
    <FormContext.Provider value={formContextValue}>
      <RegisterFieldProvider>
        <FinalForm
          {...props}
          validate={validator}
          render={({ handleSubmit, pristine, submitting, dirtyFields }) => {
            if (!pristine && !submitting && !allowLeave && !hideModal) {
              unblock.current();
              unblock.current = history.block((location) => {
                setTargetLocation(location);
                return false;
              });
            } else {
              unblock.current();
            }

            const changed = Object.keys(dirtyFields)
              .map((f) => t(f, { ns: resourceName, defaultValue: '' }))
              .filter(Boolean)
              .join(', ');
            return (
              <>
                {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                <form
                  onSubmit={readOnly ? preventDefault : handleSubmit}
                  className={className}
                >
                  {children}
                  <FormSpy<T> subscription={{ values: true }}>
                    {({ values }) => {
                      defer(() => {
                        onChange?.(values);
                      });
                      return null;
                    }}
                  </FormSpy>
                </form>
                {!hideModal && (
                  <ConfirmModal
                    shown={!!targetLocation}
                    hide={clearTargetLocation}
                    modal={{
                      content: (
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: t('form_has_unsaved_data').concat(
                              changed.length ? t('changed', { changed }) : ''
                            )
                          }}
                        />
                      ),
                      ok: t('continue'),
                      action: handleRedirect
                    }}
                  />
                )}
                {onChange && (
                  <FormSpy<T> subscription={SUBSCRIPBTION}>
                    {({ values }) => {
                      defer(() => {
                        onChange(values);
                      });
                      return null;
                    }}
                  </FormSpy>
                )}
              </>
            );
          }}
        />
      </RegisterFieldProvider>
    </FormContext.Provider>
  );
}

export * from './FormContext';
export const Form = memo(FormBase) as typeof FormBase;
